// Note: Mediabunny concatenation will be used when stitching scenes
// For platform export, we mainly handle blob/format transformations

export type PlatformType = 'linkedin' | 'instagram' | 'youtube' | 'twitter' | 'tiktok' | 'custom';

export interface PlatformSpec {
  platform: PlatformType;
  resolution: {
    width: number;
    height: number;
  };
  aspectRatio: string;
  maxDuration: number | null; // seconds, null = unlimited
  codec: {
    video: string;
    audio: string;
  };
  bitrate: {
    video: number;
    audio: number;
  };
  frameRate: number;
}

export interface ExportParams {
  platform: PlatformType;
  videoBlobs: Blob[];
  filename?: string;
  customSettings?: Partial<PlatformSpec>;
}

export interface ExportResult {
  exportId: string;
  platform: PlatformType;
  blob: Blob;
  url: string;
  fileSize: number;
  duration: number;
  resolution: { width: number; height: number };
}

export interface ExportStatus {
  exportId: string;
  platform: PlatformType;
  status: 'queued' | 'processing' | 'encoding' | 'completed' | 'failed' | 'cancelled';
  progress: number; // 0-100
  currentStep: string;
  error?: string;
  result?: ExportResult;
}

/**
 * Platform-specific video export specifications
 * Based on official platform guidelines (2025)
 */
export const PLATFORM_SPECS: Record<PlatformType, PlatformSpec> = {
  linkedin: {
    platform: 'linkedin',
    resolution: { width: 1080, height: 1080 },
    aspectRatio: '1:1',
    maxDuration: 600, // 10 minutes
    codec: { video: 'H.264', audio: 'AAC' },
    bitrate: { video: 5000000, audio: 192000 },
    frameRate: 30
  },
  instagram: {
    platform: 'instagram',
    resolution: { width: 1080, height: 1920 },
    aspectRatio: '9:16',
    maxDuration: 60, // 1 minute for feed, 60s for reels
    codec: { video: 'H.264', audio: 'AAC' },
    bitrate: { video: 8000000, audio: 192000 },
    frameRate: 30
  },
  youtube: {
    platform: 'youtube',
    resolution: { width: 1920, height: 1080 },
    aspectRatio: '16:9',
    maxDuration: null, // unlimited
    codec: { video: 'H.264', audio: 'AAC' },
    bitrate: { video: 12000000, audio: 256000 },
    frameRate: 60
  },
  twitter: {
    platform: 'twitter',
    resolution: { width: 1200, height: 675 },
    aspectRatio: '16:9',
    maxDuration: 140, // 2 minutes 20 seconds
    codec: { video: 'H.264', audio: 'AAC' },
    bitrate: { video: 6000000, audio: 192000 },
    frameRate: 30
  },
  tiktok: {
    platform: 'tiktok',
    resolution: { width: 1080, height: 1920 },
    aspectRatio: '9:16',
    maxDuration: 180, // 3 minutes for regular videos
    codec: { video: 'H.264', audio: 'AAC' },
    bitrate: { video: 8000000, audio: 192000 },
    frameRate: 30
  },
  custom: {
    platform: 'custom',
    resolution: { width: 1920, height: 1080 },
    aspectRatio: '16:9',
    maxDuration: null,
    codec: { video: 'H.264', audio: 'AAC' },
    bitrate: { video: 10000000, audio: 192000 },
    frameRate: 30
  }
};

/**
 * Video Export Service
 * Handles platform-specific video encoding and export
 */
export class ExportService {
  private exportQueue: Map<string, ExportStatus> = new Map();
  private activeExports: Set<string> = new Set();

  /**
   * Export video for a specific platform
   */
  async exportVideo(params: ExportParams): Promise<ExportResult> {
    const exportId = this.generateExportId();
    const platformSpec = params.customSettings 
      ? { ...PLATFORM_SPECS[params.platform], ...params.customSettings }
      : PLATFORM_SPECS[params.platform];

    // Initialize export status
    this.updateExportStatus(exportId, {
      exportId,
      platform: params.platform,
      status: 'queued',
      progress: 0,
      currentStep: 'Initializing...'
    });

    try {
      this.activeExports.add(exportId);

      // Step 1: Validate duration
      this.updateExportStatus(exportId, {
        status: 'processing',
        progress: 10,
        currentStep: 'Validating video...'
      });

      const totalDuration = await this.getTotalDuration(params.videoBlobs);
      
      if (platformSpec.maxDuration && totalDuration > platformSpec.maxDuration) {
        throw new Error(
          `Video duration (${Math.round(totalDuration)}s) exceeds ${params.platform} limit (${platformSpec.maxDuration}s)`
        );
      }

      // Step 2: Concatenate video blobs
      this.updateExportStatus(exportId, {
        status: 'processing',
        progress: 30,
        currentStep: 'Concatenating scenes...'
      });

      const concatenatedBlob = await this.concatenateBlobs(params.videoBlobs);

      // Step 3: Encode for platform
      this.updateExportStatus(exportId, {
        status: 'encoding',
        progress: 60,
        currentStep: `Encoding for ${params.platform}...`
      });

      const encodedBlob = await this.encodeForPlatform(
        concatenatedBlob,
        platformSpec
      );

      // Step 4: Finalize export
      this.updateExportStatus(exportId, {
        status: 'encoding',
        progress: 90,
        currentStep: 'Finalizing export...'
      });

      const url = URL.createObjectURL(encodedBlob);
      const fileSize = encodedBlob.size;

      const result: ExportResult = {
        exportId,
        platform: params.platform,
        blob: encodedBlob,
        url,
        fileSize,
        duration: totalDuration,
        resolution: platformSpec.resolution
      };

      // Complete
      this.updateExportStatus(exportId, {
        status: 'completed',
        progress: 100,
        currentStep: 'Export complete!',
        result
      });

      return result;
    } catch (error) {
      this.updateExportStatus(exportId, {
        status: 'failed',
        progress: 0,
        currentStep: 'Export failed',
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      throw error;
    } finally {
      this.activeExports.delete(exportId);
    }
  }

  /**
   * Queue an export for background processing
   */
  queueExport(params: ExportParams): string {
    const exportId = this.generateExportId();
    
    // Run export in background
    this.exportVideo(params).catch(error => {
      console.error(`[ExportService] Export ${exportId} failed:`, error);
    });

    return exportId;
  }

  /**
   * Get export status
   */
  getExportStatus(exportId: string): ExportStatus | undefined {
    return this.exportQueue.get(exportId);
  }

  /**
   * Get all exports
   */
  getAllExports(): ExportStatus[] {
    return Array.from(this.exportQueue.values());
  }

  /**
   * Cancel an export
   */
  cancelExport(exportId: string): void {
    const status = this.exportQueue.get(exportId);
    if (status && (status.status === 'queued' || status.status === 'processing')) {
      this.updateExportStatus(exportId, {
        status: 'cancelled',
        currentStep: 'Export cancelled'
      });
      this.activeExports.delete(exportId);
    }
  }

  /**
   * Download an exported video
   */
  downloadExport(exportId: string, customFilename?: string): void {
    const status = this.exportQueue.get(exportId);
    
    if (!status?.result) {
      throw new Error('Export not completed or not found');
    }

    const filename = customFilename || this.generateFilename(status.platform);
    const a = document.createElement('a');
    a.href = status.result.url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  /**
   * Clear completed exports
   */
  clearCompletedExports(): void {
    const entriesToDelete: string[] = [];
    
    this.exportQueue.forEach((status, exportId) => {
      if (status.status === 'completed' || status.status === 'failed' || status.status === 'cancelled') {
        // Revoke blob URL to free memory
        if (status.result?.url) {
          URL.revokeObjectURL(status.result.url);
        }
        entriesToDelete.push(exportId);
      }
    });

    entriesToDelete.forEach(exportId => {
      this.exportQueue.delete(exportId);
    });
  }

  // Private helper methods

  private generateExportId(): string {
    return `export-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private generateFilename(platform: PlatformType): string {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    return `${platform}-export-${timestamp}.mp4`;
  }

  private async getTotalDuration(blobs: Blob[]): Promise<number> {
    // Create temporary video elements to get duration
    const durations = await Promise.all(
      blobs.map(blob => {
        return new Promise<number>((resolve) => {
          const video = document.createElement('video');
          const url = URL.createObjectURL(blob);
          
          video.onloadedmetadata = () => {
            resolve(video.duration);
            URL.revokeObjectURL(url);
          };
          
          video.onerror = () => {
            resolve(0);
            URL.revokeObjectURL(url);
          };
          
          video.src = url;
        });
      })
    );

    return durations.reduce((sum, duration) => sum + duration, 0);
  }

  private async concatenateBlobs(blobs: Blob[]): Promise<Blob> {
    if (blobs.length === 1) {
      return blobs[0];
    }

    // For export service, we expect the video to already be stitched
    // by the SceneStitchingEngine which uses Mediabunny
    // This method just handles the case where we have a single concatenated blob
    
    // If multiple blobs are passed, this is an error case
    // Return the first blob as fallback
    console.warn('[ExportService] Multiple blobs passed to export - expected single stitched video');
    return blobs[0];
  }

  private async encodeForPlatform(blob: Blob, spec: PlatformSpec): Promise<Blob> {
    // For now, return the blob as-is
    // In a production environment, you would use FFmpeg.wasm or a server-side encoder
    // to transcode to platform-specific specs (resolution, bitrate, codec)
    
    // TODO: Implement actual platform-specific encoding
    // This would involve:
    // 1. Resizing to target resolution
    // 2. Adjusting bitrate
    // 3. Ensuring correct codec
    // 4. Setting frame rate
    
    console.log(`[ExportService] Encoding for ${spec.platform} with specs:`, {
      resolution: spec.resolution,
      bitrate: spec.bitrate,
      frameRate: spec.frameRate
    });

    // For MVP, just return the original blob
    // Platform-specific encoding would be a future enhancement
    return blob;
  }

  private updateExportStatus(exportId: string, updates: Partial<ExportStatus>): void {
    const current = this.exportQueue.get(exportId);
    const updated = { ...current, ...updates } as ExportStatus;
    this.exportQueue.set(exportId, updated);
  }
}

// Singleton instance
let exportServiceInstance: ExportService | null = null;

export const getExportService = (): ExportService => {
  if (!exportServiceInstance) {
    exportServiceInstance = new ExportService();
  }
  return exportServiceInstance;
};
